import axios, { AxiosInstance, AxiosPromise, AxiosRequestConfig } from 'axios';
import type { PropertyPath } from 'lodash';
import { merge, omit, set } from 'lodash';
import type { AnySchema } from 'yup';
import type { LazyType } from 'yup/lib/Lazy';

export type TCustomConfigExtra = { omitGlobals?: boolean };
export type TCustomConfig<ReqData extends Record<string, any> | undefined> = Omit<
  AxiosRequestConfig,
  'baseURL' | 'url' | 'data'
> & { data?: ReqData } & TCustomConfigExtra;

export interface IValidation {
  reqData?: AnySchema | LazyType<any>;
  reqParams?: AnySchema | LazyType<any>;
  resData?: AnySchema | LazyType<any>;
}

abstract class CustomApi {
  protected baseURL: string;
  protected axios: AxiosInstance;
  protected globals: AxiosRequestConfig;

  protected constructor(baseURL: string) {
    this.baseURL = baseURL;
    this.globals = {};

    const axiosInitData: AxiosRequestConfig = {
      timeout: 10000,
      baseURL,
    };

    this.axios = axios.create(axiosInitData);

    /* Handling our server responses, when errors, get verbose ones */
    this.axios.interceptors.response.use(
      (response: any) => response,
      (error: any) => Promise.reject(error.response),
    );

    /* Handling our server responses, when errors, get verbose ones */
    this.axios.interceptors.request.use((config: AxiosRequestConfig & TCustomConfigExtra) => {
      return merge(
        {
          headers: {
            ['Accept-Language']: localStorage.getItem('CURRENT_LANGUAGE') ?? undefined,
          },
        },
        config.omitGlobals ? {} : this.globals,
        config,
      );
    });
  }

  public setGlobal = (path: PropertyPath, value: string) => {
    this.globals = set(this.globals, path, value);
  };

  public removeGlobal = (path: PropertyPath) => {
    this.globals = omit(this.globals, path);
  };

  public removeGlobals = () => {
    this.globals = {};
  };

  /**
   * Function to leverage inner instance of axios to make call with interceptors based on configuration
   * @param path path to call
   * @param params axios request params
   * @returns axios promise
   */
  public request = <ResData = any, ReqData extends Record<string, any> = {}>(
    path: string,
    config: TCustomConfig<ReqData> = {},
  ): AxiosPromise<ResData> => {
    return this.axios({
      ...config,
      url: path,
      method: config?.method || 'GET',
    });
  };

  /**
   * Make request to API with validation
   * @param url url to call
   * @param config additional config
   * @param validation validate parts of request
   * @throws {AxiosError} or {Yup.ValidationError}
   * @returns promise
   */
  public requestValidate = async <ResData = any, ReqData extends Record<string, any> = {}>(
    url: string,
    config: TCustomConfig<ReqData> = {},
    validation: IValidation = {},
  ) => {
    if (validation.reqData) {
      await validation.reqData.validate(config.data);
    }

    if (validation.reqParams) {
      await validation.reqParams.validate(config.params);
    }

    const response = await this.request<ResData, ReqData>(url, config);
    if (validation.resData) {
      await validation.resData.validate(response.data);
    }

    return response;
  };
}

export default CustomApi;
